import { useState, useCallback } from 'react';
import { useMapStore } from '../../store/mapStore';

interface SignatureImportProps {
  systemId: string;
  onImportComplete?: (result: { added: string[]; updated: string[]; deleted: string[] }) => void;
}

export default function SignatureImport({ systemId, onImportComplete }: SignatureImportProps) {
  const { importSignatures } = useMapStore();
  const [rawText, setRawText] = useState('');
  const [lazyDelete, setLazyDelete] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [lastResult, setLastResult] = useState<{ added: string[]; updated: string[]; deleted: string[] } | null>(null);

  const handleImport = useCallback(async () => {
    if (!rawText.trim()) return;

    setIsImporting(true);
    setLastResult(null);

    try {
      const result = await importSignatures(systemId, rawText, lazyDelete);
      if (result) {
        setLastResult(result);
        setRawText('');
        onImportComplete?.(result);
      }
    } finally {
      setIsImporting(false);
    }
  }, [systemId, rawText, lazyDelete, importSignatures, onImportComplete]);

  // Handle paste event
  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Let the default paste happen first
      setTimeout(() => {
        // Auto-import if the pasted content looks valid
        const pastedText = e.clipboardData.getData('text');
        const lines = pastedText.trim().split('\n');
        const validLines = lines.filter((line) => {
          const parts = line.split('\t');
          return parts.length >= 2 && /^[A-Z]{3}-\d{3}$/i.test(parts[0].trim());
        });

        // If more than half the lines look valid, show a hint
        if (validLines.length > 0 && validLines.length >= lines.length * 0.5) {
          // Could auto-import here, but let user click the button
        }
      }, 0);
    },
    []
  );

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
      <h3 className="mb-3 font-semibold text-white">Import Signatures</h3>

      <p className="mb-3 text-xs text-gray-400">
        Copy signatures from EVE Online (Probe Scanner window) and paste them below.
      </p>

      <textarea
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        onPaste={handlePaste}
        placeholder="Paste signatures here...&#10;ABC-123&#9;Cosmic Signature&#9;Wormhole&#9;100.0%&#10;DEF-456&#9;Cosmic Signature&#9;Data Site&#9;75.2%"
        className="w-full h-32 rounded bg-gray-700 p-2 text-sm text-white placeholder-gray-500 font-mono resize-none"
        disabled={isImporting}
      />

      <div className="mt-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={lazyDelete}
            onChange={(e) => setLazyDelete(e.target.checked)}
            className="rounded bg-gray-700"
            disabled={isImporting}
          />
          <span>Remove signatures not in paste</span>
          <span className="text-xs text-gray-500" title="When enabled, signatures not present in the pasted text will be deleted">
            (?)
          </span>
        </label>

        <button
          onClick={handleImport}
          disabled={!rawText.trim() || isImporting}
          className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition"
        >
          {isImporting ? 'Importing...' : 'Import'}
        </button>
      </div>

      {/* Result summary */}
      {lastResult && (
        <div className="mt-3 rounded bg-gray-700/50 p-2 text-xs">
          <div className="flex gap-4">
            {lastResult.added.length > 0 && (
              <span className="text-green-400">
                +{lastResult.added.length} added
              </span>
            )}
            {lastResult.updated.length > 0 && (
              <span className="text-yellow-400">
                ~{lastResult.updated.length} updated
              </span>
            )}
            {lastResult.deleted.length > 0 && (
              <span className="text-red-400">
                -{lastResult.deleted.length} removed
              </span>
            )}
            {lastResult.added.length === 0 && lastResult.updated.length === 0 && lastResult.deleted.length === 0 && (
              <span className="text-gray-400">No changes</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
